import { Billing } from '../billing/billing'
import * as Account from '../account/id'
import { Platform } from '..'

export namespace Alert {
    export type Type =
        | 'conversionActionsFlatline'
        | 'impressionsFlatline'
        | 'delta'
        | 'budget'
        | 'conversion'
        | 'scheduled_report'
        | 'over_budget'
        | 'smart_bidding_experiment'

    export interface Object<AlertBody = unknown, AlertChart = unknown> {
        id: number // Field "alert_id"
        hashId: string // Field "hash_alert_id"
        accountId: Account.ID
        platform: Platform.Platform
        accountName: string
        accountInitials: string
        accountCurrencyCode: Billing.CurrencyCode
        accountColour: string
        body: AlertBody
        title: string
        chart: AlertChart // Field "highcharts_data" apart from budget alert
        isCampaignLevel: boolean
        type: Type
        checked: boolean
        ts: string
    }
}

export namespace AlertNew {
    export type ID = number

    export const Level = {
        Account: 'account',
        Campaign: 'campaign',
    } as const

    export type Level = (typeof Level)[keyof typeof Level]

    export type Hash = `${AlertType}:${Account.ID}`

    export const AlertType = {
        Budget: 'budget',
        Conversion: 'conversion',
        Delta: 'delta',
        ImpressionsFlatline: 'impressionsFlatline',
        ConversionActionsFlatline: 'conversionActionsFlatline',
        RecordDays: 'record_days',
        ScheduledReport: 'scheduled_report',
        OverBudget: 'over_budget',
        SmartBiddingExperiment: 'smart_bidding_experiment',
    } as const

    export type AlertType = (typeof AlertType)[keyof typeof AlertType]

    /**
     * @name ReturnSuccess
     * @param TBody generic type corresponds to alert body
     * @description Return body when calling generateAlerts
     */
    export interface ReturnSuccess<TBody = any> {
        has_many: boolean
        alerts: ReturnSuccessSingle<TBody>[]
    }

    /**
     * @name ReturnSuccessSingle
     * @param TBody generic type corresponds to alert body
     * @description Single return body, used within ReturnSuccess
     */
    export interface ReturnSuccessSingle<TBody> {
        accountId: Account.ID
        priority: number
        alert: TBody
        alert_type: AlertType
        hash_alert_id: string
        highcharts_data?: string
        level?: Level
    }

    /**
     * @name ReturnNegative
     * @description Return body when calling generateNoAlert
     */
    export interface ReturnNegative {
        negative: true
        reason: string
    }

    /**
     * @name AlertObject
     * @param TBody generic type corresponds to alert body
     * @description Properties available on an alert object
     */
    export interface AlertObject<TBody = string> {
        alert_id: AlertNew.ID
        hash_alert_id: string
        accountId: Account.ID
        type: AlertNew.AlertType
        body: TBody
        level?: Level
        is_account_level?: boolean
    }

    export interface ExtraAlertDetails {
        accountName: string
        currencySymbol: string
        slackColor: string
    }

    export interface AlertForChart<TBody = any, HighchartsData = any> {
        accountId: Account.ID
        body: TBody
        type: AlertType
        highcharts_data: HighchartsData
    }

    export interface AlertToEmail<TBody = string> extends AlertNew.AlertObject<TBody> {
        highcharts_data: string | null
        last_updated: Date
        accountName: string
        accountColor: string
        currencySymbol: string
    }

    /**
     * @name GetEmailTemplateDataOptions
     * @param TBody generic type corresponds to alert body
     * @description Properties available for generating an email template
     */
    export interface GetEmailTemplateDataOptions<TBody = string> {
        user_id: number
        alert: AlertNew.AlertToEmail<TBody>
    }

    export interface BaseEmailData {
        domain_name: string
    }

    // TODO: move this to an Email types file
    export interface EmailTemplateData<EmailData extends BaseEmailData> {
        subject: string
        data: EmailData
    }
}
